import React from 'react';
import { AccordionElement } from '../container-phase';
import { DocumentsToSend } from './documents-to-send/DocumentsToSend';
import { LandingPageCaseDetails } from './landing-page-case-details';
import { LandingPageCaseClaimForm } from './LandingPageCaseClaimForm';
import { StatusPhase, StepStatusCaseData } from '@/interfaces';
import { DocumentRequired } from './DocumentRequired';
import { useRouter } from 'next/router';
import { getStatusStep, getStep } from '@/lib/casePhases';
import { CaseContext } from '@/context/CaseContext';
import { LoaderScreen } from '@/components/common';

interface ApplicantIntakePhaseProps {
	caseSteps: StepStatusCaseData | undefined;
	casePhase: StatusPhase | undefined;
	currentOpenStep: string[] | undefined;
	setCurrentOpenStep: React.Dispatch<
		React.SetStateAction<string[] | undefined>
	>;
}

export const ApplicantIntakePhase: React.FC<ApplicantIntakePhaseProps> = ({
	caseSteps,
	casePhase,
	currentOpenStep,
	setCurrentOpenStep,
}) => {
	const router = useRouter();
	const { step: stepParams } = router.query;
	const [isErrorMessage, setIsErrorMessage] = React.useState<boolean>(false);

	const { caseDetails } = React.useContext(CaseContext);

	React.useEffect(() => {
		if (stepParams) {
			const element = document.getElementById(stepParams as string);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}

		const handleHashChange = () => {
			const newHash = stepParams as string;
			const element = document.getElementById(newHash);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		};

		window.addEventListener('hashchange', handleHashChange);

		return () => window.removeEventListener('hashchange', handleHashChange);
	}, [stepParams]);

	const landingPageCaseDetailsStep = getStep(
		'Landing Page Case Details',
		casePhase?.steps
	);

	const landingPageCaseClaimFormStep = getStep(
		'Landing Page Case Claim Form',
		casePhase?.steps
	);

	const documentRequiredStep = getStep(
		'Documents Requested (Optional)',
		casePhase?.steps
	);

	const documentsToSendStep = getStep('Documents to Send', casePhase?.steps);

	const isDocumentOptional = !caseDetails?.is_document_required;

	if (!caseDetails) return <LoaderScreen />;

	return (
		<>
			<AccordionElement
				id={`1`}
				label="Landing Page Case Details"
				step="Landing Page Case Details"
				phase={casePhase}
				stepCase={landingPageCaseDetailsStep}
				statusStep={getStatusStep(
					'Landing Page Case Details',
					casePhase?.steps
				)}
				isErrorMessage={isErrorMessage}
				setIsErrorMessage={setIsErrorMessage}
				currentOpenStep={currentOpenStep}
				setCurrentOpenStep={setCurrentOpenStep}
			>
				<LandingPageCaseDetails
					caseCategory={caseDetails?.category?.name}
					phaseId={casePhase?.fase_id || 0}
					stepCase={landingPageCaseDetailsStep}
					caseDetails={caseDetails}
				/>
			</AccordionElement>
			<AccordionElement
				id={`2`}
				label="Landing Page Case Claim Form"
				phase={casePhase}
				stepCase={landingPageCaseClaimFormStep}
				statusStep={getStatusStep(
					'Landing Page Case Claim Form',
					casePhase?.steps
				)}
				isErrorMessage={isErrorMessage}
				setIsErrorMessage={setIsErrorMessage}
				currentOpenStep={currentOpenStep}
				setCurrentOpenStep={setCurrentOpenStep}
			>
				<LandingPageCaseClaimForm
					phaseId={casePhase?.fase_id || 0}
					stepCase={landingPageCaseClaimFormStep}
					caseDetails={caseDetails}
				/>
			</AccordionElement>
			<AccordionElement
				id={`3`}
				label="Documents Requested (Optional)"
				phase={casePhase}
				stepCase={documentRequiredStep}
				statusStep={getStatusStep(
					'Documents Requested (Optional)',
					casePhase?.steps
				)}
				isErrorMessage={isDocumentOptional ? false : isErrorMessage}
				setIsErrorMessage={setIsErrorMessage}
				currentOpenStep={currentOpenStep}
				setCurrentOpenStep={setCurrentOpenStep}
			>
				<DocumentRequired
					phaseId={casePhase?.fase_id || 0}
					stepCase={documentRequiredStep}
				/>
			</AccordionElement>
			<AccordionElement
				id="4"
				label="Documents to Send"
				phase={casePhase}
				step={stepParams as string}
				statusStep={getStatusStep('Documents to Send', casePhase?.steps)}
				isErrorMessage={isErrorMessage}
				setIsErrorMessage={setIsErrorMessage}
				currentOpenStep={currentOpenStep}
				setCurrentOpenStep={setCurrentOpenStep}
				stepCase={documentsToSendStep}
			>
				<DocumentsToSend
					caseId={String(caseDetails?.id)}
					caseSteps={caseSteps}
					statusStep={getStatusStep('Documents to Send', casePhase?.steps)}
				/>
			</AccordionElement>
		</>
	);
};
